import { Directionality } from '@angular/cdk/bidi';
import {
  Overlay,
  OverlayConfig,
  OverlayContainer,
  OverlayRef,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType, TemplatePortal } from '@angular/cdk/portal';
import { Location } from '@angular/common';
import {
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  Optional,
  SkipSelf,
  TemplateRef,
  StaticProvider,
} from '@angular/core';
import { defer, Observable, of as observableOf, Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { DialogConfig } from './dialog-config';
import { DialogContainerComponent } from './dialog-container/dialog-container.component';
import { DialogRef } from './dialog-ref';

interface AnimationConfig {
  duration: number;
  timing: string;
  initialTransform: string;
  finalTransform: string;
  maxWidth: string
}


/** Injection token that can be used to access the data that was passed in to a dialog. */
export const DIALOG_DATA = new InjectionToken<any>('FinraDialogData');

/** Injection token that can be used to specify default dialog options. */
export const DIALOG_DEFAULT_OPTIONS =
  new InjectionToken<DialogConfig>('finra-dialog-default-options');

/** Injection token that determines the scroll handling while the dialog is open. */
export const DIALOG_SCROLL_STRATEGY =
  new InjectionToken<() => ScrollStrategy>('finra-dialog-scroll-strategy');

/** @docs-private */
export function DIALOG_SCROLL_STRATEGY_FACTORY(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

/** @docs-private */
export function DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: Overlay):
  () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

/** @docs-private */
export const DIALOG_SCROLL_STRATEGY_PROVIDER = {
  provide: DIALOG_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY,
};


/**
 * Service to open Material Design modal dialogs.
 */
@Injectable()
export class DialogService implements OnDestroy {
  private openDialogsAtThisLevel: DialogRef<any>[] = [];
  private readonly afterAllClosedAtThisLevel = new Subject<void>();
  private readonly afterOpenedAtThisLevel = new Subject<DialogRef<any>>();
  private ariaHiddenElements = new Map<Element, string | null>();
  private scrollStrategy: () => ScrollStrategy;
  private readonly DEFAULT_ANIMATION: AnimationConfig = {
    duration: 300,
    timing: 'ease-in',
    initialTransform: 'translateX(100%)',
    finalTransform: 'translateX(0%)',
    maxWidth:''
  };

  private isMobile(): boolean{
    return window.matchMedia('(max-width: 768px)').matches;
  }
  /** Keeps track of the currently-open dialogs. */
  get openDialogs(): DialogRef<any>[] {
    return this.parentDialog ? this.parentDialog.openDialogs : this.openDialogsAtThisLevel;
  }

  /** Stream that emits when a dialog has been opened. */
  get afterOpenedRef(): Subject<DialogRef<any>> {
    return this.parentDialog ? this.parentDialog.afterOpenedRef : this.afterOpenedAtThisLevel;
  }

  get afterAllClosedRef(): Subject<void> {
    const parent = this.parentDialog;
    return parent ? parent.afterAllClosedRef : this.afterAllClosedAtThisLevel;
  }

  // TODO (jelbourn): tighten the typing right-hand side of this expression.
  /**
   * Stream that emits when all open dialog have finished closing.
   * Will emit on subscribe if there are no open dialogs to begin with.
   */
  readonly afterAllClosed: Observable<void> = defer(() => this.openDialogs.length ?
    this.afterAllClosedRef :
    this.afterAllClosedRef.pipe(startWith(undefined))) as Observable<any>;

  constructor(
    private overlay: Overlay,
    private injector: Injector,
    /**
     * @deprecated `_location` parameter to be removed.
     * @breaking-change 10.0.0
     */
    @Optional() location: Location,
    @Optional() @Inject(DIALOG_DEFAULT_OPTIONS) private defaultOptions: DialogConfig,
    @Inject(DIALOG_SCROLL_STRATEGY) scrollStrategy: any,
    @Optional() @SkipSelf() private parentDialog: DialogService,
    private overlayContainer: OverlayContainer) {
    this.scrollStrategy = scrollStrategy;
  }

  /**
   * Opens a modal dialog containing the given component.
   * @param componentOrTemplateRef Type of the component to load into the dialog,
   *     or a TemplateRef to instantiate as the dialog content.
   * @param config Extra configuration options.
   * @returns Reference to the newly-opened dialog.
   */
  open<T, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: DialogConfig<D>): DialogRef<T, R> {

    config = applyConfigDefaults(config, this.defaultOptions || new DialogConfig());

    if (config.id && this.getDialogById(config.id)) {
      throw Error(`Dialog with id "${config.id}" exists already. The dialog id must be unique.`);
    }
    
    const overlayRef = this.createOverlay(config);

    const overlayElement = overlayRef.overlayElement;

    // Setup initial animation state
    this.setupOverlayAnimation(overlayElement);  
    const dialogContainer = this.attachDialogContainer(overlayRef, config);
    const dialogRef = this.attachDialogContent<T, R>(componentOrTemplateRef,
      dialogContainer,
      overlayRef,
      config);
     
        // Trigger animation
        this.animateOverlay(overlayElement);
    // If this is the first dialog that we're opening, hide all the non-overlay content.
    if (!this.openDialogs.length) {
      this.hideNonDialogContentFromAssistiveTechnology();
    }

    this.openDialogs.push(dialogRef);
    dialogRef.afterClosed().subscribe(() => this.removeOpenDialog(dialogRef));
    this.afterOpenedRef.next(dialogRef);

    return dialogRef;
  }

  /**
   * Closes all of the currently-open dialogs.
   */
  closeAll(): void {
    this.closeDialogs(this.openDialogs);
  }

  /**
   * Finds an open dialog by its id.
   * @param id ID to use when looking up the dialog.
   */
  getDialogById(id: string): DialogRef<any> | undefined {
    return this.openDialogs.find(dialog => dialog.id === id);
  }

  ngOnDestroy() {
    // Only close the dialogs at this level on destroy
    // since the parent service may still be active.
    this.closeDialogs(this.openDialogsAtThisLevel);
    this.afterAllClosedAtThisLevel.complete();
    this.afterOpenedAtThisLevel.complete();
  }

  /**
   * Creates the overlay into which the dialog will be loaded.
   * @param config The dialog configuration.
   * @returns A promise resolving to the OverlayRef for the created overlay.
   */
  private createOverlay(config: DialogConfig): OverlayRef {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  /**
   * Creates an overlay config from a dialog config.
   * @param dialogConfig The dialog configuration.
   * @returns The overlay configuration.
   */
  private getOverlayConfig(dialogConfig: DialogConfig): OverlayConfig {
    const state = new OverlayConfig({
      positionStrategy: this.overlay.position().global(),
      scrollStrategy: dialogConfig.scrollStrategy || this.overlay.scrollStrategies.block(),
      panelClass: dialogConfig.panelClass,
      hasBackdrop: dialogConfig.hasBackdrop,
      direction: dialogConfig.direction,
      minWidth: dialogConfig.minWidth,
      minHeight: dialogConfig.minHeight,
      maxWidth: !this.isMobile() ? dialogConfig.maxWidth : '',
      maxHeight: dialogConfig.maxHeight,
      disposeOnNavigation: dialogConfig.closeOnNavigation
    });

    if (dialogConfig.backdropClass) {
      state.backdropClass = dialogConfig.backdropClass;
    }

    return state;
  }

  /**
   * Attaches an MatDialogContainer to a dialog's already-created overlay.
   * @param overlay Reference to the dialog's underlying overlay.
   * @param config The dialog configuration.
   * @returns A promise resolving to a ComponentRef for the attached container.
   */
  private attachDialogContainer(overlay: OverlayRef, config: DialogConfig): DialogContainerComponent {
    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
    const injector = Injector.create({
      parent: userInjector || this.injector,
      providers: [{ provide: DialogConfig, useValue: config }]
    });
    
    const containerPortal = new ComponentPortal(DialogContainerComponent,
      config.viewContainerRef, injector, config.componentFactoryResolver);
    const containerRef = overlay.attach<DialogContainerComponent>(containerPortal);

    return containerRef.instance;
  }

  /**
   * Attaches the user-provided component to the already-created MatDialogContainer.
   * @param componentOrTemplateRef The type of component being loaded into the dialog,
   *     or a TemplateRef to instantiate as the content.
   * @param dialogContainer Reference to the wrapping MatDialogContainer.
   * @param overlayRef Reference to the overlay in which the dialog resides.
   * @param config The dialog configuration.
   * @returns A promise resolving to the MatDialogRef that should be returned to the user.
   */
  private attachDialogContent<T, R>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    dialogContainer: DialogContainerComponent,
    overlayRef: OverlayRef,
    config: DialogConfig): DialogRef<T, R> {
     
    // Create a reference to the dialog we're creating in order to give the user a handle
    // to modify and close it.
    const dialogRef =
      new DialogRef<T, R>(overlayRef, dialogContainer, config.id);

    // When the dialog backdrop is clicked, we want to close it.
    if (config.hasBackdrop) {
      overlayRef.backdropClick().subscribe(() => {
        if (!dialogRef.disableClose) {
          dialogRef.close();
        }
      });
    }

    if (componentOrTemplateRef instanceof TemplateRef) {
      dialogContainer.attachTemplatePortal(
        // tslint:disable-next-line
        new TemplatePortal<T>(componentOrTemplateRef, null!,
          { $implicit: config.data, dialogRef } as any));
    } else {
      const injector = this.createInjector<T>(config, dialogRef, dialogContainer);
      const contentRef = dialogContainer.attachComponentPortal<T>(
        new ComponentPortal(componentOrTemplateRef, config.viewContainerRef, injector));
      dialogRef.componentInstance = contentRef.instance;
    }
   
    dialogRef
      .updateSize(config.width, config.height)
      .updatePosition(config.position);

    return dialogRef;
  }

  /**
   * Creates a custom injector to be used inside the dialog. This allows a component loaded inside
   * of a dialog to close itself and, optionally, to return a value.
   * @param config Config object that is used to construct the dialog.
   * @param dialogRef Reference to the dialog.
   * @param container Dialog container element that wraps all of the contents.
   * @returns The custom injector that can be used inside the dialog.
   */
  private createInjector<T>(
    config: DialogConfig,
    dialogRef: DialogRef<T>,
    dialogContainer: DialogContainerComponent): Injector {

    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

    // The DialogContainerComponent is injected in the portal as the DialogContainerComponent and the dialog's
    // content are created out of the same ViewContainerRef and as such, are siblings for injector
    // purposes. To allow the hierarchy that is expected, the DialogContainerComponent is explicitly
    // added to the injection tokens.
    const providers: StaticProvider[] = [
      { provide: DialogContainerComponent, useValue: dialogContainer },
      { provide: DIALOG_DATA, useValue: config.data },
      { provide: DialogRef, useValue: dialogRef }
    ];

    if (config.direction &&
      (!userInjector || !userInjector.get<Directionality | null>(Directionality, null))) {
      providers.push({
        provide: Directionality,
        useValue: { value: config.direction, change: observableOf() }
      });
    }

    return Injector.create({ parent: userInjector || this.injector, providers });
  }

  /**
   * Removes a dialog from the array of open dialogs.
   * @param dialogRef Dialog to be removed.
   */
  private removeOpenDialog(dialogRef: DialogRef<any>) {
    const index = this.openDialogs.indexOf(dialogRef);

    if (index > -1) {
      this.openDialogs.splice(index, 1);

      // If all the dialogs were closed, remove/restore the `aria-hidden`
      // to a the siblings and emit to the `afterAllClosed` stream.
      if (!this.openDialogs.length) {
        this.ariaHiddenElements.forEach((previousValue, element) => {
          if (previousValue) {
            element.setAttribute('aria-hidden', previousValue);
          } else {
            element.removeAttribute('aria-hidden');
          }
        });

        this.ariaHiddenElements.clear();
        this.afterAllClosedRef.next();
      }
    }
  }

  /**
   * Hides all of the content that isn't an overlay from assistive technology.
   */
  private hideNonDialogContentFromAssistiveTechnology() {
    const overlayContainer = this.overlayContainer.getContainerElement();

    // Ensure that the overlay container is attached to the DOM.
    if (overlayContainer.parentElement) {
      const siblings = overlayContainer.parentElement.children;

      for (let i = siblings.length - 1; i > -1; i--) {
        const sibling = siblings[i];

        if (sibling !== overlayContainer &&
          sibling.nodeName !== 'SCRIPT' &&
          sibling.nodeName !== 'STYLE' &&
          !sibling.hasAttribute('aria-live')) {

          this.ariaHiddenElements.set(sibling, sibling.getAttribute('aria-hidden'));
          sibling.setAttribute('aria-hidden', 'true');
        }
      }
    }
  }

  /** Closes all of the dialogs in an array. */
  private closeDialogs(dialogs: DialogRef<any>[]) {
    let i = dialogs.length;

    while (i--) {
      // The `_openDialogs` property isn't updated after close until the rxjs subscription
      // runs on the next microtask, in addition to modifying the array as we're going
      // through it. We loop through all of them and call close without assuming that
      // they'll be removed from the list instantaneously.
      dialogs[i].close();
    }
  }
  private setupOverlayAnimation(
    overlayElement: HTMLElement, 
    animation: AnimationConfig = this.DEFAULT_ANIMATION
  ): void {
    if (!this.isMobile()) return;
    const initialStyles = {
      transform: animation.initialTransform,
      transition: `transform ${animation.duration}ms ${animation.timing}`,
      maxWidth: animation.maxWidth
    };
  
    Object.assign(overlayElement.style, initialStyles);
  }
  
  private animateOverlay(
    overlayElement: HTMLElement, 
    animation: AnimationConfig = this.DEFAULT_ANIMATION
  ): void {
    if (!this.isMobile()) return;
    requestAnimationFrame(() => {
      overlayElement.style.transform = animation.finalTransform;
    });
  }
}

/**
 * Applies default options to the dialog config.
 * @param config Config to be modified.
 * @param defaultOptions Default options provided.
 * @returns The new configuration object.
 */
function applyConfigDefaults(
  config?: DialogConfig, defaultOptions?: DialogConfig): DialogConfig {
  return { ...defaultOptions, ...config };
}
